import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';
import { JsonRPCService } from '@xengage/gw-portals-transport-js';

export default class AccountBillingDetailsService {
    /**
     * Retrieves an accounts billing summary.
     *
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof AccountBillingDetailsService
     */
    static getAccountBillingSummary(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getAccountBillingSummary', [], authHeader);
    }

    /**
     * Retrieves an invoice summary for account.
     *
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static getAccountInvoiceStreamInformation(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getAccountInvoiceStreamInformation', [], authHeader);
    }

    /**
     * Retrieves an invoice summary for account.
     *
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static getAccountBillingSummary_Ext(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getAccountBillingSummary_Ext', [], authHeader);
    }

    /**
     * Retrieves an invoice summary for account.
     *
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static getAccountBillingSnapshot_Ext(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getAccountBillingSnapshot_Ext', [], authHeader);
    }
    
    /**
     * Retrieves an invoice summary for account.
     * @param {String} accountNumber an account number to set the delivery method
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static getSelectedAccountBillingSnapshot_Ext(accountNumber, authHeader = {}){
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getSelectedAccountBillingSnapshot_Ext', [accountNumber], authHeader);
    }

    /**
     * Retrieves an invoice summary for account.
     * @param {String} accountNumber an account number to set the delivery method
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static getSelectedAccountBillingSummary_Ext(accountNumber, authHeader = {}){
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getSelectedAccountBillingSummary_Ext', [accountNumber], authHeader);
    }

    /**
     * Retrieves details of invoices for the invoice id.
     *
     * @param {String} invoiceStreamId a public-id of invoice stream data
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static getInvoiceStreamDetails(invoiceStreamId, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getInvoiceStreamDetails', [invoiceStreamId], authHeader);
    }

    /**
     * Set a payment instrument as automatic payment instrument.
     *
     * @param {String} invoiceStreamId a public-id of invoice stream data
     * @param {Object} instrument a payment instrument in PaymentInstrumentDTO
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static setPaymentGroupPaymentInformation(invoiceStreamId, instrument, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'setPaymentGroupPaymentInformation', [invoiceStreamId, instrument], authHeader);
    }

    /**
     * Pay for selected invoices with a payment instrument.
     *
     * @param {Array} invoiceIds array of public-ids of invoices
     * @param {Object} amount a payment amount in BigDecimal
     * @param {Object} instrument a payment instrument in PaymentInstrumentDTO
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static makeDirectBillPayment(invoiceIds, amount, instrument, authHeader = {}) {
        const directBillPaymentDto = {
            invoiceIds,
            amountPaid: amount,
            paymentInstrument: instrument
        };

        return JsonRPCService.send(getProxiedServiceUrl('directBillPayment'),
            'makeDirectBillPayment', [directBillPaymentDto], authHeader);
    }

    /**
     * Retrieves accounts billing delivery method
     *
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static getInvoiceDeliveryMethods(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getInvoiceDeliveryMethods', [], authHeader);
    }

    /**
     * Sets a new billing delivery method for the account
     *
     * @param {String} newDeliveryMethod a delivery method code
     * @param {String} accountNumber an account number to set the delivery method
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static setInvoiceDeliveryMethodForAccount(newDeliveryMethod, accountNumber, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'setInvoiceDeliveryMethodForAccount', [newDeliveryMethod, accountNumber], authHeader);
    }

  
    static createTransactionReportForAccount(accountNumber, additionalHeaders = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'createTransactionReportForAccount', [accountNumber], additionalHeaders);
    }

    static generatePaymentTokenForAgents(accountNumber, additionalHeaders = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'generatePaymentTokenForAgents', [accountNumber], additionalHeaders);
    }

    static getAccountDuesInformation_Ext( accountNumber,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'getAccountDuesInformation_Ext', [accountNumber], authHeader);
    }

    static getAccountInvoiceDuesInformation_Ext( accountNumber,invoiceStreamId,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'getAccountInvoiceDuesInformation_Ext', [accountNumber,invoiceStreamId], authHeader);
    }

    static getCreditCardLimitAllowedAccount(accountNumber,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'getCreditCardLimitAllowedAccount', [accountNumber], authHeader);
    }

    static getCreditCardProperties_Ext(accountNumber,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'getCreditCardProperties_Ext', [accountNumber], authHeader);
    }
    
    static getAccountDuesInformationOnAccount_Ext(accountNumber, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'getAccountDuesInformationOnAccount_Ext', [accountNumber], authHeader);
    }

    static makeAchPayment_Ext(data,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'makeAchPayment_Ext', data, authHeader);
    }

    static enableAutomaticPayment(data,accountNumber,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'enableAutomaticPayment', [data,accountNumber], authHeader);
    }

    static updateAutomaticPayment(data,accountNumber,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'updateAutomaticPayment', [data,accountNumber], authHeader);
    }

    

    static disableAutomaticPayment(accountNumber,invoiceStreamId,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'disableAutomaticPayment', [accountNumber,invoiceStreamId], authHeader);
    }

    

    static fetchOutstandingAmount(data,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'fetchOutstandingAmount', data, authHeader);
    }

    static fetchOutstandingInvoiceStreamAmount(data,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'fetchOutstandingInvoiceStreamAmount', data, authHeader);
    }
    

    static generatePaymentToken(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'generatePaymentToken',  [],authHeader);
    }

    static updateCreditCardPaymentDetails(data,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'updateCreditCardPaymentDetails',  [data],authHeader);
    }

    static getAlertsForCurrentUser_Ext(data,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'getAlertsForCurrentUser',  [data],authHeader);
    }

    static getAccountPolicyCancellationDetails(data,authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'getAccountPolicyCancellationDetails',  [data],authHeader);
    }
}
